var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { checkFilter } from "../../dag_defs.js";
import { MultiMap } from "@hyper-hyper-space/hhs3_util";
export * as mem from './flat_idx_mem_store.js';
export function addToFlatIndex(index, n, preds, ...tx) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const pred of (preds || [])) {
            yield index.addPred(n, pred, ...tx);
        }
    });
}
;
export function findMinimalCoverUsingFlatIndex(index, p) {
    return __awaiter(this, void 0, void 0, function* () {
        const minCover = new Set([...p]);
        const pending = new Set([...p]);
        //const visited = new Set<B64Hash>(); // un-comment for faster execution, but much worse memory usage
        while (pending.size > 0) {
            const n = pending.values().next().value;
            pending.delete(n);
            //visited.add(n);
            for (const pred of yield index.getPreds(n)) {
                minCover.delete(pred);
                // visiting is idempotent, so we don't need to re-visit nodes
                //if (!visited.has(pred)) {
                pending.add(pred);
                //}
            }
        }
        return minCover;
    });
}
function findAllPreds(index, p) {
    return __awaiter(this, void 0, void 0, function* () {
        const pending = new Set([...p]);
        const visited = new Set();
        while (pending.size > 0) {
            const n = pending.values().next().value;
            pending.delete(n);
            visited.add(n);
            for (const pred of yield index.getPreds(n)) {
                if (!visited.has(pred)) {
                    pending.add(pred);
                }
            }
        }
        return visited;
    });
}
export function findForkPositionUsingFlatIndex(index, a, b) {
    return __awaiter(this, void 0, void 0, function* () {
        const reachFromA = yield findAllPreds(index, a);
        const reachFromB = yield findAllPreds(index, b);
        const reachFromAB = new Set();
        for (const n of reachFromA) {
            if (reachFromB.has(n)) {
                reachFromAB.add(n);
            }
        }
        const forkA = new Set();
        const forkB = new Set();
        const common = new Set();
        for (const n of [...reachFromA]) {
            if (!reachFromB.has(n)) {
                const preds = yield index.getPreds(n);
                if (preds.size === 0) {
                    forkA.add(n);
                }
                for (const pred of preds) {
                    if (reachFromAB.has(pred)) {
                        forkA.add(n);
                        common.add(pred);
                    }
                }
            }
        }
        for (const n of [...reachFromB]) {
            if (!reachFromA.has(n)) {
                const preds = yield index.getPreds(n);
                if (preds.size === 0) {
                    forkB.add(n);
                }
                for (const pred of preds) {
                    if (reachFromAB.has(pred)) {
                        forkB.add(n);
                        common.add(pred);
                    }
                }
            }
        }
        const commonFrontier = yield findMinimalCoverUsingFlatIndex(index, reachFromAB);
        return { commonFrontier, common, forkA, forkB };
    });
}
export function findCoverWithFilterUsingFlatIndex(dag, index, from, filter, predicate) {
    return __awaiter(this, void 0, void 0, function* () {
        const preCover = new Set();
        const pending = new Set([...from]);
        const visited = new Set();
        while (pending.size > 0) {
            const n = pending.values().next().value;
            pending.delete(n);
            if (!visited.has(n)) {
                if ((yield dag.loadHeader(n)) === undefined) {
                    throw new Error('node ' + n + ' not found');
                }
                const entry = (yield dag.loadEntry(n));
                if (checkFilter(entry.meta, filter) && (!predicate || (yield predicate(n, entry)))) {
                    preCover.add(n);
                }
                else {
                    for (const pred of yield index.getPreds(n)) {
                        pending.add(pred);
                    }
                }
            }
            visited.add(n);
        }
        return findMinimalCoverUsingFlatIndex(index, preCover);
    });
}
export function findConcurrentCoverWithFilterUsingFlatIndex(store, index, from, concurrentTo, meta, predicate) {
    return __awaiter(this, void 0, void 0, function* () {
        // Create a successor map in forwardMap
        const forwardMap = new MultiMap();
        let pending = new Set([...from]);
        let visited = new Set();
        while (pending.size > 0) {
            const n = pending.values().next().value;
            pending.delete(n);
            for (const pred of yield index.getPreds(n)) {
                forwardMap.add(pred, n);
                if (!visited.has(pred)) {
                    pending.add(pred);
                }
            }
            visited.add(n);
        }
        // Use the forward map to close the concurrentTo set upwards
        pending = new Set([...concurrentTo]);
        visited = new Set();
        const notConcurrentTo = new Set([...concurrentTo]);
        while (pending.size > 0) {
            const n = pending.values().next().value;
            pending.delete(n);
            for (const succ of forwardMap.get(n) || []) {
                if (!visited.has(succ)) {
                    pending.add(succ);
                    notConcurrentTo.add(succ);
                }
            }
            visited.add(n);
        }
        // And the backwards map to close the concurrentTo set downwards
        pending = new Set([...concurrentTo]);
        visited = new Set();
        while (pending.size > 0) {
            const n = pending.values().next().value;
            pending.delete(n);
            for (const pred of yield index.getPreds(n)) {
                if (!visited.has(pred)) {
                    pending.add(pred);
                    notConcurrentTo.add(pred);
                }
            }
            visited.add(n);
        }
        // Do a search for a pre cover, starting at the "from" position backwards, ignoring the nodes in notConcurrentTo
        pending = new Set([...from]);
        visited = new Set();
        const preConcCover = new Set();
        while (pending.size > 0) {
            const n = pending.values().next().value;
            pending.delete(n);
            const entry = (yield store.loadEntry(n));
            if (!notConcurrentTo.has(n) && checkFilter(entry.meta, meta) && (!predicate || (yield predicate(n, entry)))) {
                preConcCover.add(n);
            }
            else {
                for (const pred of yield index.getPreds(n)) {
                    if (!visited.has(pred)) {
                        pending.add(pred);
                    }
                }
            }
            visited.add(n);
        }
        return findMinimalCoverUsingFlatIndex(index, preConcCover);
    });
}
export function createFlatIndex(store, indexStore) {
    return {
        index: function (node, after, ...tx) {
            return addToFlatIndex(indexStore, node, after, ...tx);
        },
        findMinimalCover(p) {
            return findMinimalCoverUsingFlatIndex(indexStore, p);
        },
        findForkPosition: function (a, b) {
            return findForkPositionUsingFlatIndex(indexStore, a, b);
        },
        findCoverWithFilter: function (from, meta, predicate) {
            return findCoverWithFilterUsingFlatIndex(store, indexStore, from, meta, predicate);
        },
        findConcurrentCoverWithFilter: function (from, concurrentTo, meta, predicate) {
            return findConcurrentCoverWithFilterUsingFlatIndex(store, indexStore, from, concurrentTo, meta, predicate);
        },
        getIndexStore: () => indexStore
    };
}
;
